// Builds the CSS-custom-property string injected via MUI's
// `MuiCssBaseline.styleOverrides`.  Two callers:
//
//   - `index.js` builds the canonical block at module load time
//     (covers `:root` for the active scheme + the inverse-scheme
//     block for `[data-scheme]` overrides + media-query responsive
//     swaps).
//   - `TenantThemeProvider` builds a brand-override block when a
//     tenant config supplies brand colors; it writes that to a
//     scoped `<style id="tenant-brand-tokens">` tag so the override
//     wins over `:root` without rebuilding the MUI theme.
//
// Tokens are organized in three layers (primitives, M3 palette,
// semantic+component) so dark mode and white-label can swap the
// minimum needed.

import { generateBrandPalette } from './generateBrandPalette';
import { LIGHT_PALETTE, DARK_PALETTE } from './palettes';
import { ODL_STYLE_GUIDE } from './styleGuide';

// Static primitives that never change across schemes / tenants.
const PRIMITIVES = `
    --radius-sm: 8px;
    --radius-md: 10px;
    --radius-lg: 14px;
    --radius-xl: 18px;

    --space-0: 0px;
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 20px;
    --space-6: 24px;
    --space-8: 32px;

    --font-family-sans: '${ODL_STYLE_GUIDE.typography.family}', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-xs: 0.75rem;
    --font-sm: 0.875rem;
    --font-md: 0.9375rem;
    --font-lg: 1.125rem;
    --font-xl: 1.5rem;

    --weight-normal: 400;
    --weight-medium: 500;
    --weight-semibold: 600;
    --weight-bold: 700;

    --color-gray-50: ${ODL_STYLE_GUIDE.neutral.background};
    --color-gray-100: ${ODL_STYLE_GUIDE.interactive.hover};
    --color-gray-200: ${ODL_STYLE_GUIDE.neutral.divider};
    --color-gray-300: ${ODL_STYLE_GUIDE.neutral.border};
    --color-gray-400: ${ODL_STYLE_GUIDE.interactive.disabled};
    --color-gray-500: ${ODL_STYLE_GUIDE.neutral.textMuted};
    --color-gray-600: ${ODL_STYLE_GUIDE.neutral.textSecondary};
    --color-gray-800: ${ODL_STYLE_GUIDE.neutral.textPrimary};
    --color-gray-900: ${ODL_STYLE_GUIDE.neutral.textPrimary};

    --color-white: ${ODL_STYLE_GUIDE.neutral.surface};

    --icon-size: ${ODL_STYLE_GUIDE.icon.size}px;
    --icon-size-sm: 20px;
    --icon-control-size: 40px;

    /* Motion — components reference these instead of literal ms values
       so timing can be globally tuned (and reduced for prefers-reduced-motion). */
    --duration-fast: 120ms;
    --duration-normal: 150ms;
    --duration-slow: 180ms;
    --duration-collapse: 350ms;
    --easing-standard: cubic-bezier(0.2, 0, 0, 1);
    --easing-emphasized: cubic-bezier(0.05, 0.7, 0.1, 1);
    --easing-decelerate: cubic-bezier(0, 0, 0, 1);
    --easing-accelerate: cubic-bezier(0.3, 0, 1, 1);
`;

// Brand defaults — overridden at runtime by TenantThemeProvider when
// a tenant supplies its own colors / logo.  These are the fallback
// values used when no tenant config is loaded.
const BRAND_DEFAULTS = `
    --brand-primary: var(--md-primary);
    --brand-on-primary: var(--md-on-primary);
    --brand-secondary: var(--md-secondary);
    --brand-on-secondary: var(--md-on-secondary);
    --brand-accent: var(--md-tertiary);
    --brand-name: 'Outdoorlink';
    --brand-name-long: 'Outdoorlink, Inc.';
`;

// Builds the M3 palette block for a given scheme.
const m3Block = palette => `
    --md-primary: ${palette.primary};
    --md-primary-rgb: ${palette.primaryRgb.replaceAll(',', '')};
    --md-on-primary: ${palette.onPrimary};
    --md-primary-container: ${palette.primaryContainer};
    --md-on-primary-container: ${palette.onPrimaryContainer};
    --md-secondary: ${palette.secondary};
    --md-on-secondary: ${palette.onSecondary};
    --md-secondary-container: ${palette.secondaryContainer};
    --md-on-secondary-container: ${palette.onSecondaryContainer};
    --md-tertiary: ${palette.tertiary};
    --md-on-tertiary: ${palette.onTertiary};
    --md-tertiary-container: ${palette.tertiaryContainer};
    --md-on-tertiary-container: ${palette.onTertiaryContainer};
    --md-error: ${palette.error};
    --md-on-error: ${palette.onError};
    --md-error-container: ${palette.errorContainer};
    --md-on-error-container: ${palette.onErrorContainer};
    --md-background: ${palette.background};
    --md-on-background: ${palette.onBackground};
    --md-surface: ${palette.surface};
    --md-on-surface: ${palette.onSurface};
    --md-surface-variant: ${palette.surfaceVariant};
    --md-on-surface-variant: ${palette.onSurfaceVariant};
    --md-outline: ${palette.outline};
    --md-outline-variant: ${palette.outlineVariant};
    --md-shadow: ${palette.shadow};
    --md-scrim: ${palette.scrim};
    --md-inverse-surface: ${palette.inverseSurface};
    --md-inverse-on-surface: ${palette.inverseOnSurface};
    --md-inverse-primary: ${palette.inversePrimary};
    --md-surface-dim: ${palette.surfaceDim};
    --md-surface-bright: ${palette.surfaceBright};
    --md-surface-container-lowest: ${palette.surfaceContainerLowest};
    --md-surface-container-low: ${palette.surfaceContainerLow};
    --md-surface-container: ${palette.surfaceContainer};
    --md-surface-container-high: ${palette.surfaceContainerHigh};
    --md-surface-container-highest: ${palette.surfaceContainerHighest};
`;

// Tenant colors own the expressive roles (actions, selected states, focus,
// and tonal accents), while app chrome stays neutral. This prevents a brand
// seed from turning every page, card, grid, and divider into a different tint.
const BRAND_ACCENT_ROLES = [
  'primary',
  'primaryRgb',
  'onPrimary',
  'primaryContainer',
  'onPrimaryContainer',
  'secondary',
  'onSecondary',
  'secondaryContainer',
  'onSecondaryContainer',
  'tertiary',
  'onTertiary',
  'tertiaryContainer',
  'onTertiaryContainer',
];

// A deliberately compact neutral ramp: canvas, work surface, and inset/hover
// surface. Several M3 container roles intentionally resolve to the same value
// so legacy `--md-surface-*` consumers do not recreate a many-shade UI.
const NEUTRAL_CHROME = {
  // Light values are the ODL UI style guide neutrals: Background #F8FAFC,
  // Surface #FFFFFF, Border #E7EAF0, Divider #EEF2F6, Text Primary #0F172A,
  // Text Secondary #64748B, Text Muted #94A3BB, Hover #F1F5F9.
  light: {
    background: ODL_STYLE_GUIDE.neutral.background,
    onBackground: ODL_STYLE_GUIDE.neutral.textPrimary,
    surface: ODL_STYLE_GUIDE.neutral.surface,
    onSurface: ODL_STYLE_GUIDE.neutral.textPrimary,
    surfaceVariant: ODL_STYLE_GUIDE.neutral.divider,
    onSurfaceVariant: ODL_STYLE_GUIDE.neutral.textSecondary,
    outline: ODL_STYLE_GUIDE.interactive.disabled,
    outlineVariant: ODL_STYLE_GUIDE.neutral.border,
    // Not named by the style guide: an emphasis edge, and the edge a field
    // draws at rest and under the pointer.
    borderStrong: '#B6C0CE',
    borderControl: '#A3AEBC',
    borderControlHover: '#8A95A5',
    shadow: ODL_STYLE_GUIDE.neutral.textPrimary,
    inverseSurface: ODL_STYLE_GUIDE.neutral.textPrimary,
    inverseOnSurface: ODL_STYLE_GUIDE.neutral.background,
    surfaceDim: '#E2E8F0',
    surfaceBright: ODL_STYLE_GUIDE.neutral.surface,
    surfaceContainerLowest: ODL_STYLE_GUIDE.neutral.surface,
    surfaceContainerLow: '#FBFCFE',
    surfaceContainer: ODL_STYLE_GUIDE.interactive.hover,
    surfaceContainerHigh: ODL_STYLE_GUIDE.neutral.divider,
    surfaceContainerHighest: ODL_STYLE_GUIDE.neutral.border,
  },
  // Dark is one slate ladder rather than two repeated values: on a near-black
  // page an ambient shadow reads as nothing, so elevation has to be carried by
  // surface lightness. Every neighbouring step is a perceptible move, which is
  // what lets a card, the nav rail and a hovered row differ without each one
  // drawing its own border. Text sits a little below maximum contrast on
  // purpose — #FFFFFF-adjacent copy on black haloes and is tiring to read.
  dark: {
    background: '#0B0F15',
    onBackground: '#E7ECF3',
    surface: '#161C24',
    onSurface: '#E7ECF3',
    surfaceVariant: '#242D38',
    onSurfaceVariant: '#B7C2D0',
    outline: '#8E9BAC',
    outlineVariant: '#3C4754',
    borderStrong: '#4A5563',
    borderControl: '#525E6D',
    borderControlHover: '#6D7A8B',
    shadow: '#000000',
    inverseSurface: '#E7ECF3',
    inverseOnSurface: '#161C24',
    surfaceDim: '#0B0F15',
    surfaceBright: '#333D4C',
    surfaceContainerLowest: '#10151B',
    surfaceContainerLow: '#1C232C',
    surfaceContainer: '#222A35',
    surfaceContainerHigh: '#2A3341',
    surfaceContainerHighest: '#333D4C',
  },
};

// The PDF defines these interaction roles independently of M3. Stock ODL
// light mode uses the exact documented values. White-label tenants retain the
// neutral hover/disabled roles but derive selection, focus, and links from the
// tenant's accent palette so branding still owns meaningful interaction cues.
const mixHex = (foreground, background, amount) => {
  const parse = value => value.match(/[a-f\d]{2}/gi)?.map(channel => Number.parseInt(channel, 16));
  const foregroundRgb = parse(foreground);
  const backgroundRgb = parse(background);
  if (!foregroundRgb || !backgroundRgb) return foreground;
  const channel = index => Math.round(foregroundRgb[index] * amount + backgroundRgb[index] * (1 - amount));
  return `#${[0, 1, 2].map(index => channel(index).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
};

const composeInteractionRoles = (palette, brandPalette, scheme, chrome) => {
  if (scheme === 'dark') {
    return {
      divider: chrome.surfaceVariant,
      textMuted: chrome.outline,
      interactiveHover: chrome.surfaceContainerHigh,
      interactiveSelected: mixHex(palette.primary, chrome.surface, 0.18),
      interactiveFocusRing: mixHex(palette.primary, chrome.surface, 0.42),
      interactiveDisabled: chrome.outlineVariant,
      interactiveDisabledBg: chrome.surfaceContainerLow,
      link: palette.primary,
      linkHover: palette.onPrimaryContainer,
    };
  }

  if (brandPalette) {
    return {
      divider: ODL_STYLE_GUIDE.neutral.divider,
      textMuted: ODL_STYLE_GUIDE.neutral.textMuted,
      interactiveHover: ODL_STYLE_GUIDE.interactive.hover,
      interactiveSelected: mixHex(palette.primaryContainer, chrome.surface, 0.65),
      interactiveFocusRing: mixHex(palette.primary, chrome.surface, 0.24),
      interactiveDisabled: ODL_STYLE_GUIDE.interactive.disabled,
      interactiveDisabledBg: '#F4F6F9',
      link: palette.primary,
      linkHover: palette.onPrimaryContainer,
    };
  }

  return {
    divider: ODL_STYLE_GUIDE.neutral.divider,
    textMuted: ODL_STYLE_GUIDE.neutral.textMuted,
    interactiveHover: ODL_STYLE_GUIDE.interactive.hover,
    interactiveSelected: ODL_STYLE_GUIDE.interactive.selected,
    interactiveFocusRing: ODL_STYLE_GUIDE.interactive.focusRing,
    interactiveDisabled: ODL_STYLE_GUIDE.interactive.disabled,
    interactiveDisabledBg: '#F4F6F9',
    link: ODL_STYLE_GUIDE.interactive.link,
    linkHover: ODL_STYLE_GUIDE.interactive.linkHover,
  };
};

// Compose the palette consumed by both MUI and CSS variables. Brand-generated
// palettes contribute accent roles only; neutral chrome and readable text stay
// stable across tenants so both theming paths remain byte-for-byte aligned.
export const composeAppPalette = (basePalette, brandPalette, scheme) => {
  const composed = { ...basePalette };
  if (brandPalette) {
    BRAND_ACCENT_ROLES.forEach(role => {
      if (brandPalette[role]) composed[role] = brandPalette[role];
    });
  }
  const chrome = NEUTRAL_CHROME[scheme] || NEUTRAL_CHROME.light;
  const interactionRoles = composeInteractionRoles(composed, brandPalette, scheme, chrome);
  return { ...composed, ...chrome, ...interactionRoles };
};

// Extra roles that are part of the ODL design contract but not Material 3's
// generated Scheme object. Keeping them next to the M3 block lets runtime
// tenant overrides update CSS variables and the MUI palette from one composed
// palette object.
const interactionBlock = palette => `
    --divider-color: ${palette.divider};
    --text-muted-color: ${palette.textMuted};
    --interactive-hover: ${palette.interactiveHover};
    --interactive-selected: ${palette.interactiveSelected};
    --interactive-focus-ring: ${palette.interactiveFocusRing};
    --interactive-disabled: ${palette.interactiveDisabled};
    --interactive-disabled-bg: ${palette.interactiveDisabledBg};
    --border-strong-color: ${palette.borderStrong};
    --border-control-color: ${palette.borderControl};
    --border-control-hover-color: ${palette.borderControlHover};
    --link-color: ${palette.link};
    --link-hover-color: ${palette.linkHover};
`;

// Semantic + component layer — these reference --md-* / --color-* and
// stay the same across schemes (the underlying M3 vars do the work).
// A handful of light-only ad-hoc literals (shadows and status colors) are
// scheme-aware via separate dark overrides.
const SEMANTIC_AND_COMPONENT = `
    /* ---- Semantic ---- */
    --surface-primary: var(--md-surface);
    --surface-secondary: var(--interactive-hover);
    --surface-page: var(--md-background);
    --app-content-bg: var(--surface-page);

    /* Four rungs, weakest first: a hairline inside a component, a
       structural edge, an emphasis edge, and the control edge a field or
       outlined button owes 3:1 against its own background. */
    --border-subtle: var(--divider-color);
    --border-default: var(--md-outline-variant);
    --border-strong: var(--border-strong-color);
    --border-control: var(--border-control-color);
    --border-control-hover: var(--border-control-hover-color);

    --text-primary: var(--md-on-surface);
    --text-secondary: var(--md-on-surface-variant);
    --text-muted: var(--text-muted-color);

    /* Ambient elevation stays separate from borders. Structural edges and
       controls opt into border roles explicitly; cards read primarily by depth. */
    --shadow-sm: 0 1px 2px rgb(15 23 42 / 0.05), 0 5px 14px rgb(15 23 42 / 0.08);
    --shadow-md: 0 2px 5px rgb(15 23 42 / 0.07), 0 12px 28px rgb(15 23 42 / 0.11);
    --shadow-lg: 0 6px 12px rgb(15 23 42 / 0.08), 0 20px 48px rgb(15 23 42 / 0.14);
    --shadow-dropdown: 0 8px 30px rgb(15 23 42 / 0.16), 0 2px 6px rgb(15 23 42 / 0.08);
    --shadow-raised-top: 0 -8px 24px -12px rgb(15 23 42 / 0.18);
    /* The pale guide ring supplements, but never replaces, a strong primary
       focus edge. This keeps keyboard focus visible on white surfaces. */
    --shadow-focus: 0 0 0 1px var(--surface-primary), 0 0 0 3px var(--md-primary), 0 0 0 6px var(--interactive-focus-ring);
    --shadow-raised: 0 1px 2px rgb(15 23 42 / 0.08), 0 3px 8px rgb(15 23 42 / 0.07), inset 0 1px 0 rgb(255 255 255 / 0.44);
    --shadow-raised-hover: 0 2px 4px rgb(15 23 42 / 0.10), 0 7px 16px rgb(15 23 42 / 0.10), inset 0 1px 0 rgb(255 255 255 / 0.56);
    --shadow-raised-active: 0 1px 2px rgb(15 23 42 / 0.08), inset 0 1px 3px rgb(15 23 42 / 0.16);
    --button-shadow: 0 1px 2px rgb(15 23 42 / 0.08), 0 3px 8px rgb(15 23 42 / 0.07);
    --button-shadow-hover: 0 2px 4px rgb(15 23 42 / 0.10), 0 7px 16px rgb(15 23 42 / 0.10);
    --button-shadow-active: 0 1px 2px rgb(15 23 42 / 0.08);
    --shadow-drawer-left: 10px 0 28px -18px rgb(15 23 42 / 0.42);
    --shadow-drawer-right: -8px 0 24px -20px rgb(15 23 42 / 0.34);
    --shadow-drawer-top: 0 8px 24px -20px rgb(15 23 42 / 0.34);
    --shadow-drawer-bottom: 0 -8px 24px -20px rgb(15 23 42 / 0.34);
    --shadow-header: 0 1px 10px rgb(15 23 42 / 0.07);
    --shadow-inset: inset 0 1px 3px rgb(15 23 42 / 0.12);

    /* Status bar — Capacitor StatusBar reads this; tracks scheme automatically. */
    --color-statusbar-bg: var(--md-surface);
    --color-statusbar-fg: var(--md-on-surface);

    /* Status palette — the one semantic source for ok/warning/bad/info/
       attention/neutral state colors (chips, dots, map markers, banners).
       Per state: base (dots/markers), -text (readable-on-tint), -tint
       (chip background wash). Base hues follow the ODL UI style guide
       (Active/Warning/Critical/Maintenance/Inactive); -text stays one
       shade darker so small badge text keeps AA contrast on its tint.
       Dark overrides keep text/status marks readable. */
    --status-ok: ${ODL_STYLE_GUIDE.status.active};
    --status-ok-text: #15803D;
    --status-ok-tint: ${ODL_STYLE_GUIDE.success.light};
    --status-ok-border: #BBF7D0;
    --status-warning: ${ODL_STYLE_GUIDE.status.warning};
    --status-warning-text: #B45309;
    --status-warning-tint: #FFFBEB;
    --status-warning-border: #FDE68A;
    --status-bad: ${ODL_STYLE_GUIDE.status.critical};
    --status-bad-text: #B91C1C;
    --status-bad-tint: #FEF2F2;
    --status-bad-border: #FECACA;
    --status-info: #0284C7;
    --status-info-text: #0369A1;
    --status-info-tint: #F0F9FF;
    --status-info-border: #BAE6FD;
    --status-attention: ${ODL_STYLE_GUIDE.status.maintenance};
    --status-attention-text: #6D28D9;
    --status-attention-tint: #F5F3FF;
    --status-attention-border: #DDD6FE;
    --status-neutral: #64748B;
    --status-neutral-text: #475569;
    --status-neutral-tint: #F1F5F9;
    --status-neutral-border: #E2E8F0;

    /* Stat-card gradients — the "active" fill on a clickable summary
       stat tile (ethernet/firmware/operations dashboards). One set for
       both schemes, hue-aligned with the status palette above; the
       dashboards' GRADIENTS map (features/dashboards/chipStyles.js)
       resolves through these. */
    --stat-gradient-info: linear-gradient(135deg, #0284c7 0%, #38bdf8 100%);
    --stat-gradient-success: linear-gradient(135deg, #059669 0%, #10b981 100%);
    --stat-gradient-warning: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
    --stat-gradient-error: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
    --stat-gradient-error-deep: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
    --stat-gradient-orange: linear-gradient(135deg, #ea580c 0%, #fb923c 100%);

    /* ---- Component ---- */
    --page-px: var(--space-4);
    --page-py: var(--space-2);
    --page-gap: var(--space-3);

    --title-size: var(--font-xl);
    --title-weight: var(--weight-semibold);
    --title-color: var(--text-primary);
    --subtitle-color: var(--text-secondary);
    --title-bar-py: var(--space-2);
    --title-bar-height: 40px;
    --title-action-bg: var(--surface-primary);
    --title-action-border: transparent;
    --title-action-hover: var(--surface-secondary);

    --card-radius: var(--radius-md);
    --card-border: var(--border-subtle);
    --card-bg: var(--surface-primary);
    --card-bg-clickable: var(--surface-primary);
    --card-shadow: var(--shadow-sm);
    --card-shadow-hover: var(--shadow-md);
    --card-padding: var(--space-3);
    /* Shared ambient elevation for tappable cards and raised controls. The
       pressed state is shallow and inset without the old directional ledge. */
    --card-shadow-raised: var(--shadow-raised);
    --card-shadow-raised-hover: var(--shadow-raised-hover);
    --card-shadow-raised-active: var(--shadow-raised-active);
    /* Leading icon "squircle" on card headers (summary cards, blades). */
    --card-icon-radius: 9px;

    --grid-radius: var(--radius-md);
    --grid-border: var(--border-subtle);
    --grid-header-bg: var(--surface-primary);
    --grid-header-color: var(--md-on-surface);
    --grid-header-border: var(--border-subtle);
    --grid-header-shadow: 0 1px 0 var(--grid-header-border);
    --grid-row-hover: var(--interactive-hover);
    --grid-row-selected: var(--interactive-selected);
    --grid-row-selected-hover: var(--interactive-selected);
    --grid-row-alt-bg: var(--surface-primary);
    --grid-row-alt-bg-tinted: var(--surface-page);
    --grid-name-cell-bg: var(--surface-primary);
    --grid-name-cell-bg-alt: var(--surface-page);
    --grid-pinned-divider: var(--border-default);
    --grid-cell-border: var(--border-subtle);

    --grid-toolbar-bg: var(--surface-primary);
    --grid-toolbar-border: transparent;
    --grid-toolbar-radius: var(--radius-lg);
    --grid-toolbar-px: var(--space-3);
    --grid-toolbar-py: var(--space-2);
    --grid-toolbar-gap: var(--space-3);

    /* Compact M3 surface hierarchy: the rail uses one container step, the app
       canvas uses Background, and cards/grids use Surface. */
    --sidebar-bg: var(--md-surface-container);
    --sidebar-border: var(--border-default);
    --sidebar-shadow: none;
    --sidebar-text: var(--md-on-surface);
    --sidebar-text-muted: var(--md-on-surface-variant);
    --sidebar-text-active: var(--md-on-surface);
    --sidebar-item-active-text: var(--md-on-primary-container);
    --sidebar-item-weight: var(--weight-medium);
    --sidebar-item-active-weight: var(--weight-bold);
    --sidebar-control-bg: var(--surface-page);
    --sidebar-control-hover: var(--md-surface-container-high);
    --sidebar-control-shadow: none;
    --sidebar-search-bg: var(--surface-primary);
    --sidebar-search-border: var(--border-default);
    --sidebar-search-border-hover: var(--border-strong);
    --sidebar-search-active-bg: var(--md-primary-container);
    --sidebar-search-active-border: var(--interactive-focus-ring);
    --sidebar-search-icon: var(--md-primary);
    --sidebar-search-shadow: var(--shadow-sm);
    --sidebar-context-active: var(--md-primary-container);
    --sidebar-context-icon-size: calc(var(--space-6) + var(--space-1));
    --sidebar-app-tray-bg: var(--md-surface-dim);
    --sidebar-app-tray-shadow: var(--shadow-inset);
    --sidebar-app-font-size: 0.625rem;
    --sidebar-app-icon-size: calc(var(--icon-size-sm) - var(--space-1));
    --sidebar-footer-divider: var(--sidebar-divider);
    --sidebar-footer-shadow: none;
    --sidebar-item-hover: var(--md-surface-container-high);
    --sidebar-item-active: var(--md-primary-container);
    --sidebar-item-radius: var(--radius-sm);
    /* Active-route indicator bar — painted by the MuiListItemButton theme
       override on any item carrying aria-current="page". */
    --nav-active-indicator: var(--md-primary);
    /* Section divider (horizontal rule between groups) and nested
       indent rule (vertical guide on collapsed children).  Both
       resolve to outline-variant so they adapt to light/dark and to
       brand-overridden palettes via the M3 chain. */
    --sidebar-divider: var(--border-subtle);
    --sidebar-nest-rule: var(--border-subtle);

    /* Loading skeletons — one shared, scheme-aware tone so skeletons read the
       same everywhere (consumed by the MuiSkeleton theme override). */
    --skeleton-bg: var(--surface-secondary);

    --button-radius: var(--radius-sm);
    --button-outline-border: var(--border-strong);
    --button-outline-border-hover: var(--md-primary);
    --button-disabled-bg: var(--interactive-disabled-bg);
    --button-disabled-color: var(--interactive-disabled);

    --input-bg: var(--surface-primary);
    --input-border: var(--border-control);
    --input-border-hover: var(--border-control-hover);
    --input-focus-border: var(--md-primary);
    /* Text entry reads as a recessed tray against the raised controls it sits
       beside; the focus halo stacks outside it rather than replacing it. */
    --input-shadow: var(--shadow-inset);
    --input-focus-shadow: 0 0 0 3px var(--interactive-focus-ring);
    --input-disabled-bg: var(--interactive-disabled-bg);
    --input-disabled-color: var(--interactive-disabled);
    --input-placeholder: var(--text-muted);

    --status-chip-height: 24px;
    --status-chip-radius: 6px;
    --status-chip-dot-size: 6px;
    --status-chip-font-size: 0.6875rem;
    --status-chip-gap: 5px;
    --status-chip-px: 7px;

    --raised-platform: var(--surface-secondary);
    --raised-platform-hover: var(--surface-primary);
    --raised-platform-offset: 2px;

    --filter-chip-height: 32px;
    --filter-chip-radius: var(--button-radius);
    --filter-chip-font: var(--font-sm);
    --filter-popover-radius: var(--radius-md);

    --site-mobile-bar-bg: var(--surface-primary);
    --site-mobile-bar-meta-bg: var(--surface-page);
    --site-mobile-bar-shadow: var(--shadow-header);
    --site-rail-bg: var(--card-bg);
    --site-rail-shadow: var(--card-shadow);
    --site-rail-shadow-gutter: var(--space-2);
    --site-details-drawer-radius: var(--radius-xl);

    /* Floating grid-selection action bar — deliberately fixed dark
       chrome (doesn't invert with scheme), same treatment as a
       Snackbar. */
    --selection-bar-bg: #424242;
    --selection-bar-fg: #fff;
    --selection-bar-divider: rgba(255, 255, 255, 0.2);
    --selection-bar-hover: rgba(255, 255, 255, 0.14);
    --selection-bar-disabled-fg: rgba(255, 255, 255, 0.4);

    --toolbar-gap: var(--space-2);
`;

const RESPONSIVE_BLOCK = `
  @media (max-width: 899.95px) {
    :root {
      --page-px: var(--space-3);
      --page-py: var(--space-1);
      --page-gap: var(--space-2);
      --title-size: 1.25rem;
      --title-weight: var(--weight-bold);
      --card-padding: var(--space-3);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :root {
      --duration-fast: 0ms;
      --duration-normal: 0ms;
      --duration-slow: 0ms;
      --duration-collapse: 0ms;
    }
  }
`;

// Dark-scheme remap for the grayscale primitives.  Lots of legacy code
// (and a few new sites) reach for `var(--color-white)` and
// `var(--color-gray-50/100/…)` as a "near-background surface" intent
// rather than as a literal color.  Flipping these in dark mode keeps
// those callsites visually correct without rewriting every component.
//
// The mapping mirrors M3 surface elevation: white ↔ lowest container,
// gray-50/100 ↔ low containers, gray-200/300 ↔ outlines.  Text grays
// (600+) flip to light tones.
const GRAYSCALE_DARK_REMAP = `
    --color-white: #161C24;
    --color-gray-50: #1C232C;
    --color-gray-100: #222A35;
    --color-gray-200: #2A3341;
    --color-gray-300: #3C4754;
    --color-gray-400: #505D6C;
    --color-gray-500: #6D7A8B;
    --color-gray-600: #8E9BAC;
    --color-gray-800: #B7C2D0;
    --color-gray-900: #E7ECF3;
`;

// Dark surfaces need stronger ambient separation and lighter semantic status
// colors. These override only scheme-sensitive literals; component tokens keep
// following the same semantic references as light mode.
const DARK_SEMANTIC_OVERRIDES = `
    --shadow-sm: 0 2px 4px rgb(0 0 0 / 0.30), 0 8px 20px rgb(0 0 0 / 0.28);
    --shadow-md: 0 4px 10px rgb(0 0 0 / 0.36), 0 16px 34px rgb(0 0 0 / 0.38);
    --shadow-lg: 0 8px 18px rgb(0 0 0 / 0.38), 0 24px 56px rgb(0 0 0 / 0.34);
    --shadow-dropdown: 0 10px 34px rgb(0 0 0 / 0.44), 0 2px 8px rgb(0 0 0 / 0.32);
    --shadow-raised-top: 0 -8px 26px -12px rgb(0 0 0 / 0.48);
    --shadow-focus: 0 0 0 1px var(--surface-primary), 0 0 0 3px var(--md-primary), 0 0 0 6px var(--interactive-focus-ring);
    --shadow-raised: 0 1px 2px rgb(0 0 0 / 0.32), 0 4px 10px rgb(0 0 0 / 0.26), inset 0 1px 0 rgb(255 255 255 / 0.08);
    --shadow-raised-hover: 0 2px 4px rgb(0 0 0 / 0.36), 0 8px 18px rgb(0 0 0 / 0.32), inset 0 1px 0 rgb(255 255 255 / 0.11);
    --shadow-raised-active: 0 1px 2px rgb(0 0 0 / 0.28), inset 0 1px 3px rgb(0 0 0 / 0.48);
    --button-shadow: 0 1px 2px rgb(0 0 0 / 0.32), 0 4px 10px rgb(0 0 0 / 0.26);
    --button-shadow-hover: 0 2px 4px rgb(0 0 0 / 0.36), 0 8px 18px rgb(0 0 0 / 0.32);
    --button-shadow-active: 0 1px 2px rgb(0 0 0 / 0.28);
    --shadow-drawer-left: 8px 0 26px -18px rgb(0 0 0 / 0.64);
    --shadow-drawer-right: -8px 0 26px -18px rgb(0 0 0 / 0.64);
    --shadow-drawer-top: 0 8px 26px -18px rgb(0 0 0 / 0.64);
    --shadow-drawer-bottom: 0 -8px 26px -18px rgb(0 0 0 / 0.64);
    --shadow-header: 0 1px 12px rgb(0 0 0 / 0.28);
    --shadow-inset: inset 0 1px 3px rgb(0 0 0 / 0.42);
    --sidebar-footer-shadow: none;

    --status-ok: #4ADE80;
    --status-ok-text: #4ADE80;
    --status-ok-tint: rgb(74 222 128 / 0.14);
    --status-ok-border: rgb(74 222 128 / 0.30);
    --status-warning: #FBBF24;
    --status-warning-text: #FBBF24;
    --status-warning-tint: rgb(251 191 36 / 0.14);
    --status-warning-border: rgb(251 191 36 / 0.30);
    --status-bad: #F87171;
    --status-bad-text: #F87171;
    --status-bad-tint: rgb(248 113 113 / 0.14);
    --status-bad-border: rgb(248 113 113 / 0.30);
    --status-info: #38BDF8;
    --status-info-text: #38BDF8;
    --status-info-tint: rgb(56 189 248 / 0.14);
    --status-info-border: rgb(56 189 248 / 0.30);
    --status-attention: #A78BFA;
    --status-attention-text: #A78BFA;
    --status-attention-tint: rgb(167 139 250 / 0.14);
    --status-attention-border: rgb(167 139 250 / 0.30);
    --status-neutral: #94A3B8;
    --status-neutral-text: #94A3B8;
    --status-neutral-tint: rgb(148 163 184 / 0.14);
    --status-neutral-border: rgb(148 163 184 / 0.30);
    --grid-header-border: var(--border-default);
    --grid-header-shadow: 0 1px 0 var(--grid-header-border), 0 6px 14px rgb(0 0 0 / 0.14);
`;

// Build the full design-tokens CSS string.
//
// Strategy: emit primitives + semantic/component once on `:root`, then
// emit scheme-specific `--md-*` palettes (and the grayscale remap)
// guarded by `[data-scheme]` attribute and the `prefers-color-scheme`
// media query so we get automatic dark mode for users who haven't
// explicitly toggled.
export const buildDesignTokens = () => `
  :root {
    color-scheme: light;
    ${PRIMITIVES}
    ${m3Block(composeAppPalette(LIGHT_PALETTE, null, 'light'))}
    ${interactionBlock(composeAppPalette(LIGHT_PALETTE, null, 'light'))}
    ${BRAND_DEFAULTS}
    ${SEMANTIC_AND_COMPONENT}
  }

  :root[data-scheme="dark"] {
    color-scheme: dark;
    ${m3Block(composeAppPalette(DARK_PALETTE, null, 'dark'))}
    ${interactionBlock(composeAppPalette(DARK_PALETTE, null, 'dark'))}
    ${GRAYSCALE_DARK_REMAP}
    ${DARK_SEMANTIC_OVERRIDES}
  }

  /* Note: no prefers-color-scheme fallback.  ThemeSchemeProvider
     defaults to 'light' so users on a dark-mode OS don't get
     auto-flipped into dark mode.  The dark palette only activates
     when the user explicitly chooses 'dark' (or 'system' AND the OS
     is dark) — both paths route through data-scheme="dark" on
     <html>, never through this CSS layer alone. */

  ${RESPONSIVE_BLOCK}
`;

// Build a brand-override block.  Returns a CSS string suitable for
// dropping into a scoped `<style>` tag (no selectors — caller wraps).
//
// When a tenant supplies a brand primary, Material color utilities generate
// accessible accent roles for both schemes. composeAppPalette keeps the app's
// chrome, text, and outlines neutral while substituting those brand accents.
// --brand-* tokens follow automatically via the BRAND_DEFAULTS `var(--md-*)`
// references. If generation fails, only safe metadata is emitted so CSS vars
// and the MUI palette continue using the same static accents.
// Primaries that mean "stock ODL brand, not a white-label choice": the
// palettes.js light seed itself, plus the legacy baked default that
// long-standing TenantConfig rows still carry for tenants that never
// customized their colors. Reseeding from either would replace the
// style-guide-pinned accent roles with M3 tone-40 approximations — skip
// the reseed and let the pinned defaults rule.
const LEGACY_DEFAULT_BRAND_PRIMARY = '#2196F3';

export const isDefaultBrandPrimary = hexColor => {
  if (typeof hexColor !== 'string') return false;
  const normalized = hexColor.trim().toUpperCase();
  return normalized === LIGHT_PALETTE.primary.toUpperCase() || normalized === LEGACY_DEFAULT_BRAND_PRIMARY;
};

export const buildBrandTokens = brand => {
  if (!brand) return '';

  // Scheme-agnostic brand metadata (name / accent) applies in both.
  const metaLines = [];
  if (brand.accent) metaLines.push(`--brand-accent: ${brand.accent};`);
  if (brand.name) metaLines.push(`--brand-name: '${brand.name.replace(/'/g, "\\'")}';`);
  if (brand.nameLong) metaLines.push(`--brand-name-long: '${brand.nameLong.replace(/'/g, "\\'")}';`);

  // Preferred path: generate a complete tonal brand palette, then merge only
  // its expressive roles into the stable neutral app palette.
  const shouldReseed = Boolean(brand.primary) && !isDefaultBrandPrimary(brand.primary);
  const lightBrandPalette = shouldReseed ? generateBrandPalette(brand.primary, brand.secondary, 'light') : null;
  const darkBrandPalette = shouldReseed ? generateBrandPalette(brand.primary, brand.secondary, 'dark') : null;
  if (lightBrandPalette && darkBrandPalette) {
    const lightPalette = composeAppPalette(LIGHT_PALETTE, lightBrandPalette, 'light');
    const darkPalette = composeAppPalette(DARK_PALETTE, darkBrandPalette, 'dark');
    return [
      `:root { ${metaLines.join(' ')} ${m3Block(lightPalette)} ${interactionBlock(lightPalette)} }`,
      `:root[data-scheme="dark"] { ${m3Block(darkPalette)} ${interactionBlock(darkPalette)} }`,
    ].join('\n');
  }

  if (metaLines.length === 0) return '';
  return `:root { ${metaLines.join(' ')} }`;
};
